import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from "react";
import { Project, CreateProjectRequest } from "../api/models";
import {
    getProjectsApi,
    createProjectApi,
    getProjectApi,
    updateProjectApi,
    deleteProjectApi,
    getCompanyProjects,
    updateProjectVisibility as updateProjectVisibilityApi
} from "../api/api";
import { getCurrentUser } from "../api/currentUser";
import { useMsalAuth, getToken } from "../authConfig";
import { useMsal } from "@azure/msal-react";
import { upsertProjectList } from "./projectState";

interface ProjectContextType {
    projects: Project[];
    currentProject: Project | null;
    isLoading: boolean;
    error: string | null;
    companyProjects: Project[];
    companyProjectsLoading: boolean;
    loadProjects: () => Promise<void>;
    upsertProject: (project: Project) => void;
    updateProjectsLocally: (updateProject: (project: Project) => Project) => void;
    createProject: (name: string, locationId: string, responsibleUserOids?: string[], locationName?: string) => Promise<Project>;
    updateProject: (projectId: string, updates: { name?: string; locationId?: string | null; locationName?: string | null }) => Promise<void>;
    deleteProject: (projectId: string) => Promise<void>;
    setCurrentProject: (project: Project | null) => void;
    refreshCurrentProject: (projectId: string, idToken?: string) => Promise<boolean>;
    getAllProjectChatIds: () => string[];
    loadCompanyProjects: () => Promise<void>;
    updateProjectVisibility: (projectId: string, visibility: "private" | "company") => Promise<void>;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [currentProject, setCurrentProjectState] = useState<Project | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [companyProjects, setCompanyProjects] = useState<Project[]>([]);
    const [companyProjectsLoading, setCompanyProjectsLoading] = useState(false);
    const [userHasCompany, setUserHasCompany] = useState<boolean | null>(null);
    const projectsRequestId = useRef(0);
    const currentProjectRequestId = useRef(0);

    const client = useMsalAuth ? useMsal().instance : undefined;

    const loadProjects = async () => {
        const requestId = ++projectsRequestId.current;
        try {
            setIsLoading(true);
            setError(null);

            const token = client ? await getToken(client) : undefined;
            const response = await getProjectsApi(token);
            if (projectsRequestId.current === requestId) {
                setProjects(response.projects || []);
            }
        } catch (err) {
            console.error("Failed to load projects:", err);
            if (projectsRequestId.current === requestId) {
                setError(err instanceof Error ? err.message : "Failed to load projects");
            }
        } finally {
            if (projectsRequestId.current === requestId) {
                setIsLoading(false);
            }
        }
    };

    const upsertProject = (project: Project) => {
        projectsRequestId.current += 1;
        currentProjectRequestId.current += 1;
        setIsLoading(false);
        setProjects(current => upsertProjectList(current, project));
        setCurrentProjectState(current => (current?.id === project.id ? { ...current, ...project } : current));
    };

    const updateProjectsLocally = (updateProject: (project: Project) => Project) => {
        projectsRequestId.current += 1;
        currentProjectRequestId.current += 1;
        setIsLoading(false);
        setProjects(current => current.map(updateProject));
        setCurrentProjectState(current => (current ? updateProject(current) : current));
    };

    const setCurrentProject = (project: Project | null) => {
        currentProjectRequestId.current += 1;
        setCurrentProjectState(project);
    };

    const createProject = async (name: string, locationId: string, responsibleUserOids?: string[], locationName?: string): Promise<Project> => {
        try {
            setIsLoading(true);
            setError(null);

            const token = client ? await getToken(client) : undefined;
            console.log("Creating project with token:", token ? "Token present" : "No token");

            if (!token && useMsalAuth) {
                throw new Error("Authentication token not available. Please try logging in again.");
            }

            // Build request with optional assignment
            const request: CreateProjectRequest = { name };
            if (locationId) {
                request.location_id = locationId;
            }
            if (locationName) {
                request.location_name = locationName;
            }
            if (responsibleUserOids && responsibleUserOids.length > 0) {
                request.responsible_user_oids = responsibleUserOids;
            }

            const newProject = await createProjectApi(request, token);

            // Reload projects list
            await loadProjects();
            if (userHasCompany) {
                await loadCompanyProjects();
            }

            return newProject;
        } catch (err) {
            console.error("Failed to create project:", err);
            setError(err instanceof Error ? err.message : "Failed to create project");
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const updateProject = async (projectId: string, updates: { name?: string; locationId?: string | null; locationName?: string | null }): Promise<void> => {
        try {
            setIsLoading(true);
            setError(null);

            const token = client ? await getToken(client) : undefined;
            await updateProjectApi(
                projectId,
                {
                    ...(updates.name !== undefined ? { name: updates.name } : {}),
                    ...(updates.locationId !== undefined ? { location_id: updates.locationId } : {}),
                    ...(updates.locationName !== undefined ? { location_name: updates.locationName } : {})
                },
                token
            );

            // Reload projects list
            await loadProjects();
        } catch (err) {
            console.error("Failed to update project:", err);
            setError(err instanceof Error ? err.message : "Failed to update project");
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const deleteProject = async (projectId: string): Promise<void> => {
        try {
            setIsLoading(true);
            setError(null);

            const token = client ? await getToken(client) : undefined;
            await deleteProjectApi(projectId, token);

            // Reload projects list
            await loadProjects();

            // Clear current project if it was deleted
            if (currentProject?.id === projectId) {
                setCurrentProject(null);
            }
        } catch (err) {
            console.error("Failed to delete project:", err);
            setError(err instanceof Error ? err.message : "Failed to delete project");
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const refreshCurrentProject = async (projectId: string, idToken?: string) => {
        const requestId = ++currentProjectRequestId.current;
        try {
            const token = idToken ?? (client ? await getToken(client) : undefined);
            const organizationId = currentProject?.id === projectId ? (currentProject.organization_id ?? undefined) : undefined;
            const project = await getProjectApi(projectId, token, organizationId);
            if (currentProjectRequestId.current === requestId) {
                setCurrentProjectState(project);
            }
            return true;
        } catch (err) {
            console.error("Failed to refresh project:", err);
            if (currentProjectRequestId.current === requestId) {
                setError(err instanceof Error ? err.message : "Failed to refresh project");
            }
            return false;
        }
    };

    const getAllProjectChatIds = (): string[] => {
        const chatIds: string[] = [];
        projects.forEach(project => {
            if (project.chat_sessions) {
                project.chat_sessions.forEach(session => {
                    chatIds.push(session.chat_id);
                });
            }
        });
        return chatIds;
    };

    const loadCompanyProjects = async () => {
        try {
            setCompanyProjectsLoading(true);
            setError(null);

            const token = client ? await getToken(client) : undefined;
            const response = await getCompanyProjects(token);
            setCompanyProjects(response.projects || []);
        } catch (err) {
            console.error("Failed to load company projects:", err);
            setError(err instanceof Error ? err.message : "Failed to load company projects");
        } finally {
            setCompanyProjectsLoading(false);
        }
    };

    const updateProjectVisibility = async (projectId: string, visibility: "private" | "company"): Promise<void> => {
        try {
            setIsLoading(true);
            setError(null);

            const token = client ? await getToken(client) : undefined;
            await updateProjectVisibilityApi(projectId, visibility, token);

            // Reload both project lists
            await loadProjects();
            if (userHasCompany) {
                await loadCompanyProjects();
            }

            // Refresh current project if it was updated
            if (currentProject?.id === projectId) {
                await refreshCurrentProject(projectId);
            }
        } catch (err) {
            console.error("Failed to update project visibility:", err);
            setError(err instanceof Error ? err.message : "Failed to update project visibility");
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    // Check if user has company by fetching /auth/me
    useEffect(() => {
        const checkUserCompany = async () => {
            try {
                const token = client ? await getToken(client) : undefined;
                const response = await getCurrentUser({ idToken: token });

                if (response.ok) {
                    const userData = response.user;
                    const hasCompany = userData?.company_id !== null && userData?.company_id !== undefined;
                    setUserHasCompany(hasCompany);
                }
            } catch (err) {
                console.error("Failed to check user company:", err);
                setUserHasCompany(false);
            }
        };

        if (client || !useMsalAuth) {
            checkUserCompany();
        }
    }, [client]);

    // Load projects on mount
    useEffect(() => {
        if (client || !useMsalAuth) {
            loadProjects();
        }
    }, [client]);

    // Load company projects when user has company
    useEffect(() => {
        if (userHasCompany && (client || !useMsalAuth)) {
            loadCompanyProjects();
        }
    }, [userHasCompany, client]);

    return (
        <ProjectContext.Provider
            value={{
                projects,
                currentProject,
                isLoading,
                error,
                companyProjects,
                companyProjectsLoading,
                loadProjects,
                upsertProject,
                updateProjectsLocally,
                createProject,
                updateProject,
                deleteProject,
                setCurrentProject,
                refreshCurrentProject,
                getAllProjectChatIds,
                loadCompanyProjects,
                updateProjectVisibility
            }}
        >
            {children}
        </ProjectContext.Provider>
    );
};

export const useProjects = () => {
    const context = useContext(ProjectContext);
    if (context === undefined) {
        throw new Error("useProjects must be used within a ProjectProvider");
    }
    return context;
};
